import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { galleryImages } from '../data/content';
import { CTAButton } from './CTAButton';

/**
 * Galeria materiałów — karuzela sterowana wyłącznie ręcznie.
 * Bez autoplay, bez automatycznego przewijania i bez pętli.
 */
export function MaterialPreview() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const syncState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const {
      scrollLeft,
      scrollWidth,
      clientWidth
    } = track;
    setCanScrollLeft(scrollLeft > 8);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 8);
    const card = track.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : clientWidth;
    setActiveIndex(Math.min(galleryImages.length - 1, Math.round(scrollLeft / step)));
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    syncState();
    track.addEventListener('scroll', syncState, {
      passive: true
    });
    window.addEventListener('resize', syncState);
    return () => {
      track.removeEventListener('scroll', syncState);
      window.removeEventListener('resize', syncState);
    };
  }, [syncState]);
  const scrollByCard = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
    track.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
  }, []);
  const goToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
    track.scrollTo({
      left: step * index,
      behavior: 'smooth'
    });
  }, []);
  return <section className="w-full px-5 py-14 sm:py-20" data-id="element-218" data-mp="3et42my30qg42">
      <div className="mx-auto max-w-6xl" data-id="element-219" data-mp="3jn8cpq7kz49d">
        <div className="mx-auto max-w-2xl text-center" data-id="element-220" data-mp="3jojczg1thtvq">
          <span className="inline-block rounded-full border border-chem/30 bg-chem/10 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-chem-deep sm:text-[11px]" data-id="element-221" data-mp="3ojyoi1ccrdoz">
            Zobacz materiały od środka
          </span>
          <h2 className="mt-5 font-display text-2xl font-bold leading-tight text-ink sm:text-4xl" data-id="element-222" data-mp="2vyfl25xzrikk">
            Zobacz, co znajdziesz w środku
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-ink/70 sm:text-base" data-id="element-223" data-mp="34y2cbyx0b7zw">
            Gotowe do użycia materiały wizualne, karty pracy, eksperymenty i narzędzia, które
            oszczędzają czas nauczyciela.
          </p>
        </div>

        <div className="relative mt-10" data-id="element-224" data-mp="k8wjpeszkwr1">
          <ul ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-225" data-mp="24pcgrprh4on2">
            {galleryImages.map(image => <li key={image.src} className="w-[78%] shrink-0 snap-start sm:w-[46%] md:w-[31%] lg:w-[29%]" data-id="element-226" data-mp="1vvhhuzgy3ufv">
                <div className="overflow-hidden rounded-2xl border border-chem/20 bg-white p-2 shadow-sm shadow-navy/5" data-id="element-227" data-mp="1qx680oqnhjxn">
                  <img src={image.src} alt={image.alt} loading="lazy" className="block aspect-[2/3] w-full rounded-xl object-contain" data-id="element-228" data-mp="1lw8gvmnnxfk0" />
                </div>
              </li>)}
          </ul>

          {canScrollLeft && <button type="button" onClick={() => scrollByCard(-1)} aria-label="Poprzedni obraz" className="absolute left-0 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-navy text-white shadow-lg shadow-navy/25 transition-colors duration-150 ease-out hover:bg-navy-soft sm:flex" data-id="element-229" data-mp="1czrdj5ljilyt">
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-230" data-mp="1z1fhm3oqz8mv" />
            </button>}

          {canScrollRight && <button type="button" onClick={() => scrollByCard(1)} aria-label="Następny obraz" className="absolute right-0 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-navy text-white shadow-lg shadow-navy/25 transition-colors duration-150 ease-out hover:bg-navy-soft sm:flex" data-id="element-231" data-mp="241a7q50p7u6v">
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-232" data-mp="1g9welwuq3uku" />
            </button>}
        </div>

        <div className="mt-6 flex flex-col items-center gap-4" data-id="element-233" data-mp="1l3qx4wyle3ic">
          <div className="flex items-center gap-3 sm:hidden" data-id="element-234" data-mp="17bkff6fsjogs">
            {canScrollLeft && <button type="button" onClick={() => scrollByCard(-1)} aria-label="Poprzedni obraz" className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white shadow-md shadow-navy/25" data-id="element-235" data-mp="1bc3790hoouiz">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-236" data-mp="12by10to8826e" />
              </button>}
            {canScrollRight && <button type="button" onClick={() => scrollByCard(1)} aria-label="Następny obraz" className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white shadow-md shadow-navy/25" data-id="element-237" data-mp="iqxroh3ojehs">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-238" data-mp="sou216osm5eu" />
              </button>}
          </div>

          <div className="flex items-center gap-1.5" data-id="element-239" data-mp="nnekholtqh17">
            {galleryImages.map((image, i) => <button key={image.src} type="button" onClick={() => goToIndex(i)} aria-label={`Przejdź do obrazu ${i + 1}`} aria-current={i === activeIndex} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === activeIndex ? 'w-6 bg-navy' : 'w-1.5 bg-navy/25'}`} data-id="element-240" data-mp="11nfqtjkuygo2" data-mpi={i} />)}
          </div>

          <p className="text-xs font-medium text-ink/55 sm:text-sm" data-id="element-241" data-mp="16hk1actc16tw">
            Przesuń, aby zobaczyć więcej →
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-md" data-id="element-242" data-mp="1ge6lczdu3tuv">
          <CTAButton label="Chcę otrzymać pełny zestaw" data-id="element-243" data-mp="1gfhnkurmstjv" />
        </div>
      </div>
    </section>;
}